﻿import { RadarChart } from "./RadarChart";
import type { CalibrateResponse, OccupationDetail, CompareResponse } from "../types";
import { RISK_LEVELS } from "../data/constants";

function dimensionColor(score: number): string {
  if (score <= 30) return "#22c55e";
  if (score <= 50) return "#3b82f6";
  if (score <= 65) return "#eab308";
  if (score <= 80) return "#f97316";
  return "#ef4444";
}

function dimensionBgColor(score: number): string {
  if (score <= 30) return "#052e16";
  if (score <= 50) return "#0c1929";
  if (score <= 65) return "#422006";
  if (score <= 80) return "#431407";
  return "#450a0a";
}

function benchmarkDiff(yours: number, benchmark: number): string {
  const diff = yours - benchmark;
  if (diff > 10) return `高于均值 ${diff.toFixed(0)} 分`;
  if (diff < -10) return `低于均值 ${Math.abs(diff).toFixed(0)} 分`;
  return "接近均值";
}

function benchmarkDiffColor(yours: number, benchmark: number): string {
  const diff = yours - benchmark;
  if (diff > 10) return "#f97316";
  if (diff < -10) return "#22c55e";
  return "#94a3b8";
}

export function ReportView({
  result,
  detail,
  aiCompare,
  aiLoading,
  onFetchAICompare,
  onRetake,
}: {
  result: CalibrateResponse;
  detail: OccupationDetail;
  aiCompare: CompareResponse | null;
  aiLoading: boolean;
  onFetchAICompare: () => void;
  onRetake: () => void;
}) {
  const riskInfo = RISK_LEVELS[result.risk_level];
  const radarData = result.score_breakdown.map((d) => ({
    label: d.label,
    score: d.raw_score,
  }));

  return (
    <div className="min-h-screen max-w-3xl mx-auto px-8 py-16 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="font-[family-name:var(--font-heading)] text-4xl font-bold text-[#f1f5f9]">
          {detail.name}
        </h2>
        <p className="text-xl text-[#cbd5e1]">AI 时代职业发展报告</p>
        <p className="text-sm text-[#64748b]">
          基于 OpenAI、McKinsey、WEF 等权威研究数据
        </p>
      </div>

      {/* Risk Level Badge */}
      <div
        className="p-6 rounded-xl text-center border"
        style={{
          background: `linear-gradient(135deg, ${riskInfo.color}15, ${riskInfo.color}08)`,
          borderColor: `${riskInfo.color}33`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
        }}
      >
        <h3 className="text-sm font-medium text-[#94a3b8] mb-3">综合风险等级</h3>
        <span
          className="font-[family-name:var(--font-heading)] text-5xl font-bold"
          style={{ color: riskInfo.color }}
        >
          {result.risk_label}
        </span>
        <p className="text-sm text-[#94a3b8] mt-3 leading-relaxed">{riskInfo.meaning}</p>
      </div>

      {/* Total Score + Adjustments */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-5 rounded-xl bg-[#1e293b] border border-[#334155] text-center" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <div className="text-xs text-[#64748b] mb-1">综合风险分</div>
          <div className="font-[family-name:var(--font-heading)] text-3xl font-bold" style={{ color: riskInfo.color }}>
            {Math.round(result.total_score)}
          </div>
          <div className="text-xs text-[#475569] mt-1">满分 100</div>
        </div>
        <div className="p-5 rounded-xl bg-[#1e293b] border border-[#334155] text-center" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <div className="text-xs text-[#64748b] mb-1">学历调整因子</div>
          <div className="font-[family-name:var(--font-heading)] text-3xl font-bold text-[#60a5fa]">
            {result.education_adjustment.toFixed(2)}
          </div>
          <div className="text-xs text-[#475569] mt-1">OpenAI Table 6</div>
        </div>
        <div className="p-5 rounded-xl bg-[#1e293b] border border-[#334155] text-center" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <div className="text-xs text-[#64748b] mb-1">经验调整因子</div>
          <div className="font-[family-name:var(--font-heading)] text-3xl font-bold text-[#a78bfa]">
            {result.experience_adjustment.toFixed(2)}
          </div>
          <div className="text-xs text-[#475569] mt-1">OpenAI Table 7</div>
        </div>
      </div>

      {/* Radar Chart */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-2">
          五维风险雷达图
        </h3>
        <p className="text-xs text-[#64748b] mb-4">面积越大 = 风险越高，越接近边缘 = 该维度风险越高</p>
        <RadarChart dimensions={radarData} size={300} />
      </div>

      {/* 5-Dimension Score Breakdown */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-6">
          五维度风险分解
        </h3>
        <div className="space-y-4">
          {result.score_breakdown.map((dim) => (
            <div key={dim.dimension}>
              <div className="flex items-center gap-4">
                <div className="w-36 shrink-0">
                  <div className="text-sm text-[#cbd5e1] font-medium">{dim.label}</div>
                  <div className="text-xs text-[#64748b]">权重 {(dim.weight * 100).toFixed(0)}%</div>
                </div>
                <div className="flex-1 h-5 bg-[#0f172a] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${Math.min(dim.raw_score, 100)}%`,
                      maxWidth: "100%",
                      backgroundColor: dimensionColor(dim.raw_score),
                    }}
                  />
                </div>
                <span className="w-12 text-right text-sm font-semibold text-[#f1f5f9] tabular-nums">
                  {dim.raw_score.toFixed(0)}<span className="text-[10px] text-[#64748b]">/100</span>
                </span>
                <span
                  className="w-12 text-center text-xs font-medium px-1.5 py-0.5 rounded-full"
                  style={{
                    color: dimensionColor(dim.raw_score),
                    backgroundColor: dimensionBgColor(dim.raw_score),
                  }}
                >
                  {dim.raw_score <= 30 ? "低" : dim.raw_score <= 50 ? "中低" : dim.raw_score <= 65 ? "中" : dim.raw_score <= 80 ? "高" : "极高"}
                </span>
              </div>
              <p className="text-[11px] text-[#475569] ml-40 mt-0.5">{dim.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benchmark Analysis */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-5">
          对标分析
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-[#0f172a] border border-[#334155]">
            <div className="text-xs text-[#64748b] mb-2">与行业平均水平对比</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9]">
                {result.total_score.toFixed(0)}
              </span>
              <span className="text-sm text-[#64748b]">vs</span>
              <span className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#94a3b8]">
                {result.industry_benchmark.toFixed(0)}
              </span>
            </div>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{
                color: benchmarkDiffColor(result.total_score, result.industry_benchmark),
                backgroundColor: benchmarkDiffColor(result.total_score, result.industry_benchmark) + "15",
              }}
            >
              {benchmarkDiff(result.total_score, result.industry_benchmark)}
            </span>
            <p className="text-[11px] text-[#475569] mt-2">
              行业基准基于 McKinsey(2023) 行业影响分与 WEF 2025 技术转型预期综合计算。
            </p>
          </div>
          <div className="p-4 rounded-lg bg-[#0f172a] border border-[#334155]">
            <div className="text-xs text-[#64748b] mb-2">与职业类别平均水平对比</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9]">
                {result.total_score.toFixed(0)}
              </span>
              <span className="text-sm text-[#64748b]">vs</span>
              <span className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#94a3b8]">
                {result.occupation_benchmark.toFixed(0)}
              </span>
            </div>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{
                color: benchmarkDiffColor(result.total_score, result.occupation_benchmark),
                backgroundColor: benchmarkDiffColor(result.total_score, result.occupation_benchmark) + "15",
              }}
            >
              {benchmarkDiff(result.total_score, result.occupation_benchmark)}
            </span>
            <p className="text-[11px] text-[#475569] mt-2">
              职业基准基于 WEF 2025 岗位增长/衰退分类及 O*NET 暴露度中位值计算。
            </p>
          </div>
        </div>
      </div>

      {/* Cross Checks */}
      {result.cross_checks.filter((c) => c.applied).length > 0 && (
        <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
            交叉校验调整
          </h3>
          <div className="space-y-3">
            {result.cross_checks
              .filter((c) => c.applied)
              .map((cc, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: cc.adjustment > 0 ? "#43140733" : "#052e1633",
                    border: `1px solid ${cc.adjustment > 0 ? "#f9731633" : "#22c55e33"}`,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#cbd5e1]">{cc.rule}</span>
                    <span
                      className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                        cc.adjustment > 0 ? "text-[#f97316] bg-[#431407]" : "text-[#22c55e] bg-[#052e16]"
                      }`}
                    >
                      {cc.adjustment > 0 ? "+" : ""}{cc.adjustment}
                    </span>
                  </div>
                  <p className="text-xs text-[#94a3b8] mt-1">{cc.reason}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Percentile Rank */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
          百分位排名
        </h3>
        <div className="space-y-3">
          <p className="text-sm text-[#cbd5e1]">
            你的风险评分高于 <span className="font-bold text-[#f97316] text-lg">{result.percentile_rank.toFixed(0)}%</span> 的评估用户
          </p>
          <div className="h-3 bg-[#0f172a] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${result.percentile_rank}%`,
                background: "linear-gradient(90deg, #22c55e, #eab308, #f97316, #ef4444)",
              }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-[#475569]">
            <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
          </div>
        </div>
      </div>

      {/* Risk Archetype */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#f9731633]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
          风险归因类型
        </h3>
        <div className="p-4 rounded-lg bg-[#0f172a] border border-[#334155]">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#f9731615] text-[#f97316] border border-[#f9731633]">
              {result.risk_archetype.id}
            </span>
            <span className="text-lg font-bold text-[#f1f5f9]">{result.risk_archetype.name}</span>
          </div>
          <div className="space-y-2">
            <div>
              <span className="text-xs text-[#64748b]">核心矛盾</span>
              <p className="text-sm text-[#cbd5e1] mt-0.5">{result.risk_archetype.core_contradiction}</p>
            </div>
            <div>
              <span className="text-xs text-[#64748b]">盲点提醒</span>
              <p className="text-sm text-[#f97316] mt-0.5">{result.risk_archetype.blind_spot}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Findings */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
          关键发现
        </h3>
        <div className="space-y-3">
          {result.key_findings.map((kf, idx) => {
            const sevStyle =
              kf.severity === "critical"
                ? { badge: "#ef4444", bg: "#450a0a", border: "#ef444433" }
                : kf.severity === "warning"
                  ? { badge: "#f97316", bg: "#431407", border: "#f9731633" }
                  : kf.severity === "positive"
                    ? { badge: "#22c55e", bg: "#052e16", border: "#22c55e33" }
                    : { badge: "#3b82f6", bg: "#0c1929", border: "#3b82f633" };
            return (
              <div
                key={idx}
                className="p-4 rounded-lg"
                style={{ backgroundColor: sevStyle.bg, border: `1px solid ${sevStyle.border}` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase"
                    style={{ color: sevStyle.badge, backgroundColor: sevStyle.badge + "20" }}
                  >
                    {kf.severity}
                  </span>
                </div>
                <p className="text-sm text-[#f1f5f9] font-medium mb-1">{kf.conclusion}</p>
                <p className="text-xs text-[#94a3b8] mb-1">{kf.plain_language}</p>
                <p className="text-[11px] text-[#475569]">数据引用：{kf.data_citation}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transition Directions */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
          职业提升方向建议
        </h3>
        {result.transition_directions.length === 0 ? (
          <div className="p-6 rounded-lg bg-[#0f172a] border border-dashed border-[#334155] text-center">
            <p className="text-sm text-[#64748b]">完成个人评估后可查看个性化职业提升方向建议</p>
            <p className="text-xs text-[#475569] mt-1">基于你的问卷答案，系统会分析最适合你的职业提升路径</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {result.transition_directions.map((dir) => (
              <div key={dir.id} className="p-4 rounded-lg bg-[#0f172a] border border-[#334155]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-mono text-[#475569]">{dir.id}</span>
                  <span className="text-base font-bold text-[#f1f5f9]">{dir.name}</span>
                </div>
                <p className="text-sm text-[#60a5fa] mb-2">{dir.one_liner}</p>
                <p className="text-sm text-[#cbd5e1] mb-2">{dir.description}</p>
                <p className="text-xs text-[#94a3b8]">
                  <span className="text-[#64748b]">为什么适合你：</span>{dir.why_you}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Plan — 规则核心 + AI 补充 */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-5">
          行动方案
        </h3>
        {(() => {
          const hasAi = aiCompare?.mode === "dual_track" && aiCompare.comparison?.merged_actions;
          const allSteps = [...result.action_phases.short_term, ...result.action_phases.medium_term, ...result.action_phases.long_term];

          if (hasAi) {
            // AI 已加载：合并行动，共识优先
            const merged = aiCompare.comparison!.merged_actions;
            const sourceOrder: Record<string, number> = { "⭐强烈推荐": 0, "🤖AI建议": 1, "📊数据模型建议": 2 };
            const phaseLabels: Record<string, string> = { short_term: "短期", medium_term: "中期", long_term: "长期" };
            const allActions: { action: string; source: string; phase: string }[] = [];
            for (const phase of ["short_term", "medium_term", "long_term"] as const) {
              for (const item of merged[phase] || []) {
                allActions.push({ ...item, phase });
              }
            }
            allActions.sort((a, b) => (sourceOrder[a.source] ?? 9) - (sourceOrder[b.source] ?? 9));

            // 构建 title → ActionStep 查找表，规则建议保留原始详细描述
            const stepByTitle: Record<string, typeof allSteps[0]> = {};
            for (const s of allSteps) stepByTitle[s.title] = s;

            // AI path action_plan → {why, timeframe} 查找表，AI 建议显示完整信息
            const aiPlanByAction: Record<string, { why: string; timeframe: string }> = {};
            const ap = aiCompare.ai_path?.action_plan;
            if (ap) {
              for (const phase of ["short_term", "mid_term", "long_term"] as const) {
                for (const a of ap[phase] || []) {
                  if (a.action) aiPlanByAction[a.action] = { why: a.why || "", timeframe: a.timeframe || "" };
                }
              }
            }

            return (
              <div className="space-y-3">
                {allActions.map((item, i) => {
                  const matched = stepByTitle[item.action];
                  const isAiOnly = item.source === "🤖AI建议";
                  const badgeColor = item.source === "⭐强烈推荐" ? "#22c55e" : isAiOnly ? "#a78bfa" : "#3b82f6";
                  return (
                    <div key={i} className="p-4 rounded-lg bg-[#0f172a] border" style={{ borderColor: badgeColor + "33" }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ color: "#f1f5f9", backgroundColor: "#334155" }}>{i + 1}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: badgeColor, backgroundColor: badgeColor + "15" }}>{item.source}</span>
                        <span className="text-sm font-bold text-[#f1f5f9] flex-1">{item.action}</span>
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{
                          color: item.phase === "short_term" ? "#f97316" : item.phase === "medium_term" ? "#3b82f6" : "#22c55e",
                          backgroundColor: (item.phase === "short_term" ? "#f97316" : item.phase === "medium_term" ? "#3b82f6" : "#22c55e") + "15",
                        }}>{phaseLabels[item.phase] || item.phase}</span>
                      </div>
                      {matched ? (
                        <>
                          <p className="text-xs text-[#cbd5e1] mb-1.5">{matched.what}</p>
                          <p className="text-xs text-[#94a3b8] mb-1">{matched.how}</p>
                          <p className="text-[11px] text-[#22c55e]">{matched.success_criteria}</p>
                        </>
                      ) : (() => {
                        const aiDetail = aiPlanByAction[item.action];
                        return aiDetail ? (
                          <>
                            <p className="text-xs text-[#cbd5e1] mb-1.5">{aiDetail.why}</p>
                            <p className="text-[11px] text-[#a78bfa]">{aiDetail.timeframe}</p>
                          </>
                        ) : (
                          <p className="text-[11px] text-[#a78bfa]">AI 基于原始答案独立发现的补充建议</p>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            );
          }

          // AI 未加载：3 条规则核心行动 + CTA
          if (allSteps.length === 0) {
            return (
              <div className="p-6 rounded-lg bg-[#0f172a] border border-dashed border-[#334155] text-center">
                <p className="text-sm text-[#64748b]">完成个人评估后可查看关键行动建议</p>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              {allSteps.map((step, i) => {
                const pb = step.phase === "short_term" ? { text: "短期 1-3 个月", color: "#f97316" }
                  : step.phase === "medium_term" ? { text: "中期 3-12 个月", color: "#3b82f6" }
                  : { text: "长期 1-3 年", color: "#22c55e" };
                return (
                  <div key={step.id} className="p-4 rounded-lg bg-[#0f172a] border border-[#334155]">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ color: "#f1f5f9", backgroundColor: "#334155" }}>{i + 1}</span>
                      <span className="text-sm font-bold text-[#f1f5f9]">{step.title}</span>
                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded ml-auto" style={{ color: pb.color, backgroundColor: pb.color + "15" }}>{pb.text}</span>
                    </div>
                    <p className="text-xs text-[#cbd5e1] mb-1.5">{step.what}</p>
                    <p className="text-xs text-[#94a3b8] mb-1">{step.how}</p>
                    <p className="text-[11px] text-[#22c55e]">{step.success_criteria}</p>
                  </div>
                );
              })}
              {!aiCompare && (
                <div className="mt-3 p-3 rounded-lg bg-[#0f172a] border border-dashed border-[#a78bfa33] text-center">
                  <p className="text-xs text-[#a78bfa]">
                    以上为数据模型建议的 {allSteps.length} 条核心行动，点击「🤖 AI 独立分析报告」获取 AI 个性化补充建议
                  </p>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ---- AI 双轨分析 ---- */}
      {aiLoading && (
        <div className="p-6 rounded-xl bg-[#1e293b] border border-[#3b82f633] text-center" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-[#334155] rounded w-48 mx-auto" />
            <div className="h-2 bg-[#334155] rounded w-64 mx-auto" />
            <p className="text-sm text-[#64748b]">AI 正在独立分析你的答案，不参考规则引擎分数...</p>
          </div>
        </div>
      )}

      {aiCompare && aiCompare.mode === "dual_track" && (
        <>
          {/* Divergence Alert */}
          {aiCompare.divergence_alert && (
            <div className="p-4 rounded-xl border border-[#f9731633] bg-[#431407]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
              <p className="text-sm text-[#f97316] font-medium">⚠️ {aiCompare.divergence_alert}</p>
            </div>
          )}

          {/* AI Dual Score Card */}
          <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
            <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
              🤖 AI 独立评估
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-[#0f172a] border border-[#334155] text-center">
                <div className="text-xs text-[#64748b] mb-1">规则引擎</div>
                <div className="font-[family-name:var(--font-heading)] text-3xl font-bold" style={{ color: RISK_LEVELS[result.risk_level].color }}>
                  {Math.round(result.total_score)}
                </div>
                <div className="text-xs text-[#475569] mt-1">{result.risk_label}</div>
              </div>
              <div className="p-4 rounded-lg bg-[#0f172a] border border-[#334155] text-center">
                <div className="text-xs text-[#64748b] mb-1">AI 独立评分</div>
                <div className="font-[family-name:var(--font-heading)] text-3xl font-bold text-[#a78bfa]">
                  {Math.round(aiCompare.ai_result!.ai_total_score)}
                </div>
                <div className="text-xs text-[#475569] mt-1">{aiCompare.ai_result!.ai_risk_label}</div>
              </div>
              <div className="p-4 rounded-lg bg-[#0f172a] border border-[#334155] text-center">
                <div className="text-xs text-[#64748b] mb-1">偏差</div>
                <div className="font-[family-name:var(--font-heading)] text-3xl font-bold" style={{
                  color: aiCompare.comparison!.divergence_type === "consensus" ? "#22c55e" : "#f97316"
                }}>
                  {aiCompare.comparison!.divergence_type === "consensus" ? "共识" : aiCompare.comparison!.divergence_type === "ai_optimistic" ? "AI 乐观" : "AI 悲观"}
                </div>
                <div className="text-xs text-[#475569] mt-1">
                  差异 {Math.abs(aiCompare.ai_result!.ai_total_score - result.total_score).toFixed(0)} 分
                  {aiCompare.ai_result!.confidence ? ` · AI 置信度 ${(aiCompare.ai_result!.confidence * 100).toFixed(0)}%` : ""}
                </div>
              </div>
            </div>
          </div>

          {/* Dimension Divergence */}
          {aiCompare.comparison && aiCompare.comparison.dimension_divergences.length > 0 && (
            <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
              <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
                双轨五维对比
              </h3>
              <div className="space-y-3">
                {aiCompare.comparison.dimension_divergences.map((div) => {
                  const isConsensus = div.direction === "consensus";
                  return (
                    <div key={div.dimension} className="flex items-center gap-3">
                      <div className="w-24 shrink-0 text-sm text-[#cbd5e1]">{div.label}</div>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="h-3 bg-[#3b82f6] rounded-l" style={{ width: `${Math.min(div.rule_score, 100)}%`, maxWidth: "45%" }} />
                        <span className="text-[11px] text-[#64748b] w-6 text-center">{div.rule_score.toFixed(0)}</span>
                        <span className="text-[11px]" style={{ color: isConsensus ? "#22c55e" : "#f97316" }}>
                          {isConsensus ? "≈" : div.direction === "ai_optimistic" ? "←" : "→"}
                        </span>
                        <span className="text-[11px] text-[#64748b] w-6 text-center">{div.ai_score.toFixed(0)}</span>
                        <div className="h-3 bg-[#a78bfa] rounded-r" style={{ width: `${Math.min(div.ai_score, 100)}%`, maxWidth: "45%" }} />
                      </div>
                      <span className="text-[11px] w-12 text-right" style={{ color: isConsensus ? "#22c55e" : "#f97316" }}>
                        {div.diff > 0 ? "+" : ""}{div.diff.toFixed(0)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#334155]">
                <span className="text-[11px] text-[#64748b]"><span className="inline-block w-2 h-2 rounded-sm bg-[#3b82f6] mr-1" />规则引擎</span>
                <span className="text-[11px] text-[#64748b]"><span className="inline-block w-2 h-2 rounded-sm bg-[#a78bfa] mr-1" />AI 独立</span>
              </div>
            </div>
          )}

          {/* AI Path Narrative */}
          {aiCompare.ai_path && (
            <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
              <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-2">
                AI 顾问洞察
              </h3>
              <p className="text-xs text-[#64748b] mb-4">以下内容完全由 AI 基于你的原始答案独立生成，未参考规则引擎结果</p>

              {aiCompare.ai_path.highlights && aiCompare.ai_path.highlights.length > 0 && (
                <div className="space-y-2 mb-4">
                  {aiCompare.ai_path.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-[#0f172a] border border-[#334155]">
                      <span className="text-[#a78bfa] mt-0.5 shrink-0">✦</span>
                      <p className="text-sm text-[#cbd5e1]">{h}</p>
                    </div>
                  ))}
                </div>
              )}

              {aiCompare.ai_path.blind_spot && (
                <div className="p-3 rounded-lg bg-[#431407] border border-[#f9731633] mb-4">
                  <span className="text-xs text-[#f97316] font-bold">盲点提醒</span>
                  <p className="text-sm text-[#f97316] mt-0.5">{aiCompare.ai_path.blind_spot}</p>
                </div>
              )}

              {aiCompare.ai_path.next_step_today && (
                <div className="p-3 rounded-lg bg-[#052e16] border border-[#22c55e33]">
                  <span className="text-xs text-[#22c55e] font-bold">今天就能开始</span>
                  <p className="text-sm text-[#22c55e] mt-0.5">{aiCompare.ai_path.next_step_today}</p>
                </div>
              )}
            </div>
          )}

          {/* Timeline Prediction */}
          {aiCompare.ai_timeline && (
            <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
              <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">
                6 个月风险预测
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[#0f172a] border border-[#ef444433]">
                  <div className="text-xs text-[#ef4444] font-bold mb-2">不采取行动</div>
                  <div className="flex items-end gap-1 h-20">
                    {aiCompare.ai_timeline.no_action.points.map((p) => (
                      <div key={p.month} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className="w-full rounded-t-sm" style={{
                          height: `${Math.min(p.risk_score, 100) * 0.18}px`,
                          backgroundColor: "#ef4444",
                          opacity: 0.3 + (p.month / 6) * 0.7,
                        }} />
                        <span className="text-[9px] text-[#475569]">{p.month}m</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#94a3b8] mt-2">{aiCompare.ai_timeline.no_action.summary}</p>
                </div>
                <div className="p-4 rounded-lg bg-[#0f172a] border border-[#22c55e33]">
                  <div className="text-xs text-[#22c55e] font-bold mb-2">执行行动方案</div>
                  <div className="flex items-end gap-1 h-20">
                    {aiCompare.ai_timeline.with_action.points.map((p) => (
                      <div key={p.month} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className="w-full rounded-t-sm" style={{
                          height: `${Math.min(p.risk_score, 100) * 0.18}px`,
                          backgroundColor: "#22c55e",
                          opacity: 0.4 + (1 - p.month / 6) * 0.6,
                        }} />
                        <span className="text-[9px] text-[#475569]">{p.month}m</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#94a3b8] mt-2">{aiCompare.ai_timeline.with_action.summary}</p>
                </div>
              </div>
              {aiCompare.ai_timeline.key_inflection_month && (
                <p className="text-xs text-[#a78bfa] mt-3 text-center">
                  关键拐点：第 {aiCompare.ai_timeline.key_inflection_month} 个月
                </p>
              )}
              {aiCompare.ai_timeline.recommendation && (
                <p className="text-sm text-[#cbd5e1] mt-2 text-center italic">
                  {aiCompare.ai_timeline.recommendation}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Data Sources */}
      <div className="text-center space-y-1.5">
        <p className="text-xs text-[#64748b]">
          数据来源：{result.data_sources.join(" / ")}
        </p>
        <p className="text-xs text-[#475569]">
          本评估仅供参考，不构成职业建议。数据基于公开学术研究和权威机构报告，可能存在时滞。
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-center items-center gap-4">
        <button
          onClick={onRetake}
          className="px-8 py-3 rounded-full bg-[#3b82f6] text-white font-semibold hover:bg-[#2563eb] transition-colors"
          style={{ boxShadow: "0 0 24px -6px rgba(59,130,246,0.3)" }}
        >
          重新评估
        </button>
        {!aiCompare && (
          <button
            onClick={onFetchAICompare}
            disabled={aiLoading}
            className="px-8 py-3 rounded-full font-semibold transition-colors border"
            style={{
              backgroundColor: aiLoading ? "#1e293b" : "#0f172a",
              color: aiLoading ? "#64748b" : "#a78bfa",
              borderColor: aiLoading ? "#334155" : "#a78bfa33",
              boxShadow: aiLoading ? "none" : "0 0 24px -6px rgba(167,139,250,0.2)",
            }}
          >
            {aiLoading ? "AI 分析中..." : "🤖 AI 独立分析报告"}
          </button>
        )}
      </div>
    </div>
  );
}